import React, { useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useSelector } from 'react-redux'
import { IconButton, Tooltip, Typography } from '@material-ui/core'
import EditIcon from '@material-ui/icons/EditOutlined'
import DeleteIcon from '@material-ui/icons/DeleteOutline'
import LaunchIcon from '@material-ui/icons/Launch'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import { isEmpty } from 'lodash'
import { Button, Loading } from '../../root/components/common'
import { CustomSwitch2 } from '../../root/components/common/switch'
import { UserPermissions } from '../../../services/permissions'
import { getFormattedDate } from '../../../utils'
import { useBillingConfirmation } from '../hooks/use-billing-confirmation'
import BillingInfoModal from '../billing-info-modal'
import {
    useWebhooks,
    useCreateWebhook,
    useUpdateWebhook,
    useDeleteWebhook
} from '../hooks/use-webhooks'
import { WEBHOOKS_DOCS_URL } from './constants'
import WebhookEditorModal from './webhook-editor-modal'
import WebhookDeleteModal from './webhook-delete-modal'

const useStyles = makeStyles(theme => ({
    headerBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
        gap: 16
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 10
    },
    docsLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: 'none',
        border: 'none',
        padding: 0,
        color: theme.primary,
        fontSize: 13,
        fontWeight: 'bold',
        fontFamily: theme.fontFamily,
        textDecoration: 'underline',
        cursor: 'pointer',
        '&:hover': {
            opacity: 0.6
        }
    },
    rowCard: {
        padding: '14px 20px',
        marginBottom: 10,
        border: theme.border,
        borderRadius: theme.borderRadius,
        background: theme.background,
        display: 'grid',
        gridTemplateColumns:
            '125px minmax(140px, 1.6fr) minmax(160px, 2.6fr) 80px 110px 130px 80px',
        gap: 14,
        alignItems: 'center'
    },
    rowDisabled: {
        opacity: 0.65
    },
    colHeader: {
        padding: '0 21px',
        marginBottom: 8,
        display: 'grid',
        gridTemplateColumns:
            '125px minmax(140px, 1.6fr) minmax(160px, 2.6fr) 80px 110px 130px 80px',
        gap: 14,
        fontSize: 11,
        fontFamily: theme.fontFamily,
        color: theme.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.4
    },
    nameCol: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minWidth: 0
    },
    nameMain: {
        fontSize: 14,
        fontWeight: 600,
        color: theme.textPrimary,
        fontFamily: theme.fontFamily,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    nameSub: {
        fontSize: 12,
        color: theme.textSecondary,
        fontFamily: theme.fontFamily,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    endpoint: {
        fontSize: 13,
        color: theme.textPrimary,
        fontFamily: 'monospace, monospace',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0
    },
    methodPill: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '3px 8px',
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: 'monospace, monospace',
        background: theme.tertiary,
        color: theme.textPrimary,
        letterSpacing: 0.4,
        whiteSpace: 'nowrap',
        width: 'fit-content'
    },
    reasonsCell: {
        fontSize: 13,
        color: theme.textPrimary,
        fontFamily: theme.fontFamily
    },
    metaCell: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        fontSize: 12,
        color: theme.textSecondary,
        fontFamily: theme.fontFamily,
        minWidth: 0
    },
    metaPrimary: {
        fontSize: 13,
        color: theme.textPrimary,
        fontWeight: 600
    },
    metaEmpty: {
        fontSize: 13,
        color: theme.textSecondary,
        fontFamily: theme.fontFamily,
        fontStyle: 'italic'
    },
    statusCol: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minWidth: 0
    },
    statusLabel: {
        fontSize: 12,
        fontWeight: 500,
        fontFamily: theme.fontFamily,
        whiteSpace: 'nowrap'
    },
    statusOn: {
        color: theme.green,
        fontWeight: 600
    },
    statusOff: {
        color: theme.textSecondary
    },
    actionsCol: {
        display: 'flex',
        gap: 4,
        justifyContent: 'flex-end'
    },
    deleteBtn: {
        color: theme.error
    },
    actionsBar: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: 12
    },
    empty: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        padding: '48px 0',
        textAlign: 'center'
    },
    emptyTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: theme.textPrimary,
        fontFamily: theme.fontFamily
    },
    emptyBody: {
        fontSize: 13,
        color: theme.textSecondary,
        fontFamily: theme.fontFamily,
        maxWidth: 420
    },
    statusDash: {
        color: theme.textSecondary
    }
}))

function reasonLabelList(reasons, locale) {
    if (!Array.isArray(reasons) || reasons.length === 0) return ''
    return reasons.map(r => locale.reasonLabels?.[r] ?? r).join(', ')
}

function ResponseCodeBadge({ code, classes, theme }) {
    if (code == null) return <span className={classes.statusDash}>—</span>
    let color = theme.textPrimary
    if (code >= 200 && code < 300) color = theme.green
    else if (code >= 400) color = theme.error
    return <span style={{ color, fontWeight: 600 }}>{code}</span>
}

export function WebhooksContent() {
    const classes = useStyles()
    const theme = useSelector(state => state.main.theme)
    const localeAll = useSelector(state => state.main.locale)
    const locale = localeAll.integrations.webhooksTab
    const { original_customer_id: customerId } = useSelector(
        state => state.main.user
    )

    const perms = UserPermissions.location(customerId)
    if (!perms.read) return null

    const { data: webhooks, isLoading } = useWebhooks()
    const createMutation = useCreateWebhook()
    const updateMutation = useUpdateWebhook()
    const deleteMutation = useDeleteWebhook()
    const billingConfirmation = useBillingConfirmation()

    const [editorState, setEditorState] = useState({
        open: false,
        mode: 'create',
        webhook: null
    })
    const [deleteState, setDeleteState] = useState({
        open: false,
        webhook: null
    })

    const openCreate = () =>
        setEditorState({ open: true, mode: 'create', webhook: null })
    const openEdit = webhook =>
        setEditorState({ open: true, mode: 'edit', webhook })
    const closeEditor = () => setEditorState(prev => ({ ...prev, open: false }))

    const openDelete = webhook => setDeleteState({ open: true, webhook })
    const closeDelete = () => setDeleteState(prev => ({ ...prev, open: false }))

    const handleEditorSubmit = body => {
        const options = { onSuccess: () => closeEditor() }
        if (editorState.mode === 'edit') {
            updateMutation.mutate({ id: editorState.webhook.id, body }, options)
        } else {
            createMutation.mutate(body, options)
        }
    }

    const handleDeleteConfirm = () => {
        if (!deleteState.webhook) return
        deleteMutation.mutate(deleteState.webhook.id, {
            onSuccess: () => closeDelete()
        })
    }

    const handleToggleEnabled = (webhook, checked) => {
        if (!perms.update) return
        const run = () =>
            updateMutation.mutate({
                id: webhook.id,
                body: { is_enabled: checked }
            })
        if (checked && !webhook.is_enabled) {
            billingConfirmation.requestConfirmation(run)
        } else {
            run()
        }
    }

    const sorted = useMemo(() => {
        if (!Array.isArray(webhooks)) return []
        return [...webhooks].sort((a, b) => {
            if (a.is_enabled !== b.is_enabled) {
                return a.is_enabled ? -1 : 1
            }
            return (a.name ?? '').localeCompare(b.name ?? '')
        })
    }, [webhooks])

    const isSaving = createMutation.isLoading || updateMutation.isLoading

    return (
        <div>
            <div className={classes.headerBar}>
                <div className={classes.headerLeft}>
                    <a
                        className={classes.docsLink}
                        href={WEBHOOKS_DOCS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <HelpOutlineIcon style={{ fontSize: 16 }} />
                        {locale.viewDocs}
                        <LaunchIcon style={{ fontSize: 14 }} />
                    </a>
                </div>
                {perms.create && !isEmpty(sorted) && (
                    <Button onClick={openCreate}>{locale.createWebhook}</Button>
                )}
            </div>

            {isLoading && <Loading />}

            {!isLoading && !isEmpty(sorted) && (
                <React.Fragment>
                    <div className={classes.colHeader}>
                        <span>{locale.fields.status}</span>
                        <span>{locale.fields.name}</span>
                        <span>{locale.fields.endpoint}</span>
                        <span>{locale.fields.requestMethod}</span>
                        <span>{locale.fields.reasons}</span>
                        <span>{locale.fields.lastResponse}</span>
                        <span />
                    </div>
                    {sorted.map(webhook => (
                        <div
                            key={webhook.id}
                            className={`${classes.rowCard} ${
                                !webhook.is_enabled ? classes.rowDisabled : ''
                            }`}
                        >
                            <div className={classes.statusCol}>
                                <CustomSwitch2
                                    checked={Boolean(webhook.is_enabled)}
                                    onChange={(_, checked) =>
                                        handleToggleEnabled(webhook, checked)
                                    }
                                    disabled={
                                        !perms.update ||
                                        updateMutation.isLoading
                                    }
                                />
                                <span
                                    className={`${classes.statusLabel} ${
                                        webhook.is_enabled
                                            ? classes.statusOn
                                            : classes.statusOff
                                    }`}
                                >
                                    {webhook.is_enabled
                                        ? locale.statusEnabled
                                        : locale.statusDisabled}
                                </span>
                            </div>

                            <div className={classes.nameCol}>
                                <span className={classes.nameMain}>
                                    {webhook.name}
                                </span>
                                {webhook.description && (
                                    <span className={classes.nameSub}>
                                        {webhook.description}
                                    </span>
                                )}
                            </div>

                            <Tooltip
                                title={webhook.endpoint ?? ''}
                                placement="top"
                            >
                                <div className={classes.endpoint}>
                                    {webhook.endpoint}
                                </div>
                            </Tooltip>

                            <span className={classes.methodPill}>
                                {locale.methodLabels?.[
                                    webhook.request_method
                                ] ?? webhook.request_method}
                            </span>

                            <Tooltip
                                title={reasonLabelList(webhook.reasons, locale)}
                                placement="top"
                            >
                                <span className={classes.reasonsCell}>
                                    {webhook.reasons?.length ?? 0}{' '}
                                    {webhook.reasons?.length === 1
                                        ? locale.fields.reasonSingular
                                        : locale.fields.reasonPlural}
                                </span>
                            </Tooltip>

                            {webhook.last_response_code == null &&
                            webhook.last_request_timestamp == null ? (
                                <span className={classes.metaEmpty}>
                                    {locale.never}
                                </span>
                            ) : (
                                <div className={classes.metaCell}>
                                    <span className={classes.metaPrimary}>
                                        <ResponseCodeBadge
                                            code={webhook.last_response_code}
                                            classes={classes}
                                            theme={theme}
                                        />
                                    </span>
                                    {webhook.last_request_timestamp && (
                                        <span>
                                            {getFormattedDate(
                                                webhook.last_request_timestamp
                                            )}
                                        </span>
                                    )}
                                </div>
                            )}

                            <div className={classes.actionsCol}>
                                {perms.update && (
                                    <Tooltip
                                        title={locale.editWebhook}
                                        placement="top"
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={() => openEdit(webhook)}
                                            aria-label={locale.editWebhook}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {perms.delete && (
                                    <Tooltip
                                        title={locale.deleteWebhook}
                                        placement="top"
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={() => openDelete(webhook)}
                                            className={classes.deleteBtn}
                                            aria-label={locale.deleteWebhook}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    ))}
                </React.Fragment>
            )}

            {!isLoading && isEmpty(sorted) && (
                <div className={classes.empty}>
                    <Typography className={classes.emptyTitle}>
                        {locale.noWebhooksFound}
                    </Typography>
                    <Typography className={classes.emptyBody}>
                        {locale.emptyBody}
                    </Typography>
                    {perms.create && (
                        <Button onClick={openCreate}>
                            {locale.createWebhook}
                        </Button>
                    )}
                </div>
            )}

            <WebhookEditorModal
                open={editorState.open}
                mode={editorState.mode}
                webhook={editorState.webhook}
                onClose={closeEditor}
                onSubmit={handleEditorSubmit}
                isSaving={isSaving}
                canUpdate={perms.update || perms.create}
                billingConfirmation={billingConfirmation}
            />

            <WebhookDeleteModal
                open={deleteState.open}
                webhook={deleteState.webhook}
                onCancel={closeDelete}
                onConfirm={handleDeleteConfirm}
                isDeleting={deleteMutation.isLoading}
            />

            <BillingInfoModal
                open={billingConfirmation.isOpen}
                onConfirm={billingConfirmation.confirm}
                onCancel={billingConfirmation.cancel}
            />
        </div>
    )
}

export default WebhooksContent
